import React from 'react';
import { Modal, Form, Input, Select, Space, Avatar, Row, Col, Button, FormInstance, Typography } from 'antd';
import { UserOutlined, RobotOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { LifetreeDetailResponse, NodeScenario } from '../../api/models/Lifetree';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface ScenarioFormModalProps {
  open: boolean;
  onOk: () => void;
  onCancel: () => void;
  confirmLoading: boolean;
  form: FormInstance;
  title: string;
  okText: string;
  isAISample?: boolean;
  lifetreeDetail: LifetreeDetailResponse | null;
  availableNPCs: { id: string; name: string; avatar: string | null }[];
}

const ScenarioFormModal: React.FC<ScenarioFormModalProps> = ({
  open,
  onOk,
  onCancel,
  confirmLoading,
  form,
  title,
  okText,
  isAISample = false,
  lifetreeDetail,
  availableNPCs,
}) => {
  return (
    <Modal
      title={title}
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      width={1000}
      okText={okText}
      cancelText="Hủy"
      style={{ top: 20 }}
      styles={{ 
        body: { 
          maxHeight: '75vh', 
          overflow: 'auto',
          padding: '20px'
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
      >
        {isAISample && (
          <div style={{ 
            background: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '6px', 
            padding: '12px', 
            marginBottom: '16px' 
          }}>
            <Space>
              <RobotOutlined style={{ color: '#52c41a' }} />
              <Text type="secondary">Dữ liệu được tạo bởi AI - bạn có thể chỉnh sửa trước khi lưu</Text>
            </Space>
          </div>
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Tên Scenario"
              rules={[{ required: true, message: 'Vui lòng nhập tên scenario' }]}
            >
              <Input placeholder="Nhập tên scenario" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="location"
              label="Địa điểm"
              rules={[{ required: true, message: 'Vui lòng nhập địa điểm' }]}
            >
              <Input placeholder="VD: Phòng ký túc xá, Thư viện, Quán cà phê..." />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="time"
              label="Thời gian"
              rules={[{ required: true, message: 'Vui lòng nhập thời gian' }]}
            >
              <Input placeholder="VD: Buổi sáng, Tối muộn, Cuối tuần..." />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="npcIds"
              label="NPCs tham gia"
            >
              <Select
                mode="multiple"
                placeholder="Chọn NPCs tham gia scenario"
                style={{ width: '100%' }}
              >
                {availableNPCs.map(npc => (
                  <Option key={npc.id} value={npc.id}>
                    <Space>
                      <Avatar src={npc.avatar} icon={<UserOutlined />} size="small" />
                      <span>{npc.name}</span>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Mô tả Scenario"
        >
          <TextArea 
            autoSize={{ minRows: 2, maxRows: 15 }}
            placeholder="Mô tả ngắn gọn về scenario này"
          />
        </Form.Item>

        <Form.Item
          name="locationDescription"
          label="Mô tả chi tiết địa điểm"
        >
          <TextArea 
            autoSize={{ minRows: 2, maxRows: 15 }}
            placeholder="Mô tả chi tiết về bối cảnh, không gian diễn ra scenario"
          />
        </Form.Item>

        <Form.Item
          name="openingScene"
          label="Cảnh mở đầu"
          rules={[{ required: true, message: 'Vui lòng nhập cảnh mở đầu' }]}
        >
          <TextArea 
            autoSize={{ minRows: 3, maxRows: 20 }}
            placeholder="Mô tả tình huống, bối cảnh khi scenario bắt đầu"
          />
        </Form.Item>

        <Form.Item
          name="firstMission"
          label="Nhiệm vụ đầu tiên"
        >
          <TextArea 
            autoSize={{ minRows: 2, maxRows: 15 }}
            placeholder="Nhiệm vụ hoặc mục tiêu đầu tiên cho người chơi"
          />
        </Form.Item>

        <Form.Item
          label="Lời thoại mở đầu"
        >
          <Form.List name="openingLines">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={8} style={{ marginBottom: 8 }}>
                    <Col span={8}>
                      <Form.Item
                        {...restField}
                        name={[name, 'ownerId']}
                        rules={[{ required: true, message: 'Chọn nhân vật' }]}
                      >
                        <Select placeholder="Chọn nhân vật">
                          {lifetreeDetail?.characters.map(character => (
                            <Option key={character.id} value={character.id}>
                              <Space>
                                <Avatar src={character.imageUrl} icon={<UserOutlined />} size="small" />
                                <span>{character.name}</span>
                              </Space>
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={14}>
                      <Form.Item
                        {...restField}
                        name={[name, 'content']}
                        rules={[{ required: true, message: 'Nhập nội dung' }]}
                      >
                        <TextArea 
                          autoSize={{ minRows: 1, maxRows: 10 }}
                          placeholder="Nội dung lời thoại"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <Button 
                        type="text" 
                        danger 
                        icon={<DeleteOutlined />} 
                        onClick={() => remove(name)}
                      />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Thêm lời thoại
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ScenarioFormModal;
